// Lista de pendências do Manejo — funções PURAS, compartilhadas entre client e servidor.

/** Uma linha de texto colado, já limpa, pronta para a prévia "Criar N itens". */
export interface LinhaColada {
  titulo: string;
  /** Riscada (`~texto~`) vem desmarcada — é o que já foi feito no grupo. */
  marcada: boolean;
  motivo?: "riscada";
}

// Marcadores de lista: • ▸ · (e travessões) valem sem espaço depois; `-` e `*` só
// com espaço, porque `*negrito*` é formatação do WhatsApp e `-5 °C` é texto.
const MARCADOR_LIVRE = /^[•▸·–—]+\s*/;
const MARCADOR_COM_ESPACO = /^[-*]+\s+/;
const NUMERACAO = /^\d+\s*[.)]\s+/;

function tirarMarcadores(linha: string): string {
  let t = linha.trim();
  // Itera: "• 1. texto" e "- • texto" saem limpos.
  for (let i = 0; i < 4; i++) {
    const antes = t;
    t = t.replace(MARCADOR_LIVRE, "").replace(MARCADOR_COM_ESPACO, "").replace(NUMERACAO, "").trim();
    if (t === antes) break;
  }
  return t;
}

function desriscar(t: string): string | null {
  const m = /^~+\s*([^~]+?)\s*~+$/.exec(t);
  return m ? m[1].trim() : null;
}

/**
 * Transforma a mensagem colada (WhatsApp, bloco de notas) em itens candidatos.
 * Sem LLM: tira marcadores e numeração, apara espaços, descarta linhas vazias e
 * devolve DESMARCADA a linha inteira riscada (`~…~`). O cabeçalho ("Atividades em
 * andamento 🚧") vem marcado — o usuário o desmarca na prévia.
 */
export function parsearListaColada(texto: string): LinhaColada[] {
  const saida: LinhaColada[] = [];
  texto.split(/\r?\n/).forEach(bruta => {
    let t = tirarMarcadores(bruta);
    if (!t) return;
    let riscada = false;
    const dentro = desriscar(t);
    if (dentro !== null) {
      riscada = true;
      t = tirarMarcadores(dentro);
    }
    if (!t) return;
    saida.push(riscada ? { titulo: t, marcada: false, motivo: "riscada" } : { titulo: t, marcada: true });
  });
  return saida;
}

export interface OperacaoParaTitulo {
  titulo: string | null;
  atividade: { nome: string } | null;
  setores?: { setor: { nome: string } }[];
}

/** Até quantos setores aparecem no título; o resto vira "+N". */
export const MAX_SETORES_NO_TITULO = 6;

/** Ordenação natural: A2 antes de A10 (pedaços numéricos comparados como número). */
export function compararNatural(a: string, b: string): number {
  const pa = a.split(/(\d+)/);
  const pb = b.split(/(\d+)/);
  const n = Math.min(pa.length, pb.length);
  for (let i = 0; i < n; i++) {
    if (pa[i] === pb[i]) continue;
    const na = Number(pa[i]);
    const nb = Number(pb[i]);
    if (i % 2 === 1 && !isNaN(na) && !isNaN(nb)) return na - nb;
    return pa[i] < pb[i] ? -1 : 1;
  }
  return pa.length - pb.length;
}

function escaparRegex(t: string): string {
  return t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * O texto já cita TODOS estes setores como palavra inteira? ("Plantão A10" não cita
 * o A1.) Sem diferenciar maiúsculas; letras acentuadas contam como parte da palavra.
 * Regex sem escape Unicode: o typecheck do CI roda em ES3.
 */
export function tituloJaCita(texto: string, nomes: string[]): boolean {
  if (nomes.length === 0) return false;
  return nomes.every(nome => {
    const re = new RegExp("(^|[^A-Za-z0-9À-ÿ])" + escaparRegex(nome) + "($|[^A-Za-z0-9À-ÿ])", "i");
    return re.test(texto);
  });
}

/** "A1, A2" — até 6 nomes; acima disso, os 6 primeiros e "+N". */
export function compactarSetores(nomes: string[]): string {
  if (nomes.length <= MAX_SETORES_NO_TITULO) return nomes.join(", ");
  return `${nomes.slice(0, MAX_SETORES_NO_TITULO).join(", ")} +${nomes.length - MAX_SETORES_NO_TITULO}`;
}

/**
 * O título que TODA tela e a mensagem do WhatsApp mostram — uma função só:
 * `título próprio || atividade || "Sem título"`, seguido de ` · setores` (ordem
 * natural, sem repetir) — a não ser que o próprio texto já cite todos os setores.
 */
export function tituloExibicaoOperacao(op: OperacaoParaTitulo): string {
  const base = op.titulo?.trim() || op.atividade?.nome || "Sem título";
  const vistos: Record<string, true> = {};
  const nomes = (op.setores ?? [])
    .map(s => s.setor.nome.trim())
    .filter(n => n.length > 0 && !vistos[n] && (vistos[n] = true))
    .sort(compararNatural);
  if (nomes.length === 0 || tituloJaCita(base, nomes)) return base;
  return `${base} · ${compactarSetores(nomes)}`;
}
